import React from 'react';
import { Box, Heading, View } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import ModuleCard from './ModuleCard';
import { getModuleState } from '../../../common/utils/moduleStateLegacy';
import { getColorForModule } from '../../../common/utils/getColorForModule';

const ModuleList = ({ modules, handleOpenModule }) => {
  const { t } = useTranslation();

  // Step 1: Calculate state for each module
  const modulesWithState = modules.map((module) => ({
    // Extract gradingDueDate BEFORE replacing the state object with state string
    ...module,
    gradingDueDate: module.state?.gradingDue || null,
    state: getModuleState(module, modules),
    course: module.courses[0], // Since we know there's always exactly one course
  }));

  // Step 2: Group modules by their state
  const modulesByState = modulesWithState.reduce((acc, mod) => {
    if (!acc[mod.state]) {
      acc[mod.state] = [];
    }
    acc[mod.state].push(mod);
    return acc;
  }, {});

  // Define state sections for display
  const moduleSections = [
    {
      title: t('student.course.modulestages.active'),
      state: 'active',
      data: modulesByState.active || [],
    },
    {
      title: t('student.course.modulestages.locked'),
      state: 'locked',
      data: modulesByState.locked || [],
    },
    {
      title: t('student.course.modulestages.submitted'),
      state: 'submitted',
      data: modulesByState.submitted || [],
    },
    {
      title: t('student.course.modulestages.graded'),
      state: 'graded',
      data: modulesByState.graded || [],
    },
  ];

  // Step 3: Group modules by course within each state
  const groupByCourse = (mods) => mods.reduce((acc, mod) => {
    const courseId = mod.course.id;
    if (!acc[courseId]) {
      acc[courseId] = { courseTitle: mod.course.title, modules: [] };
    }
    acc[courseId].modules.push(mod);
    return acc;
  }, {});

  return (
    <View testID="module-list-container">
      {moduleSections
        .filter((section) => section.data.length > 0)
        .map((section) => (
          <Box key={section.title} mb={24}>
            <Heading testID={`section-title-${section.state}`} size="md" color="$textDark" mb={24}>
              {section.title}
            </Heading>
            {Object.entries(groupByCourse(section.data)).map(([courseId, { courseTitle, modules }]) => (
              <Box key={courseId} mb={24}>
                <Heading size="sm" color="$textDark" mb={16}>
                  {courseTitle}
                </Heading>
                <Box display="flex" flexWrap="wrap" flexDirection="row" gap={38}>
                  {modules.map((module) => (
                    <ModuleCard
                      testID="module-card"
                      key={`${courseId}-${module.id}`}
                      id={module.id}
                      courseTitle={module.course.title}
                      title={module.title}
                      description={module.metadata.description}
                      numTasks={module?.tasks?.length || 0}
                      lecturer={`${module.author?.title} ${module.author?.name}`}
                      state={module.state}
                      gradingDueDate={module.gradingDueDate}
                      color={getColorForModule(module)}
                      handleOpenModule={() => handleOpenModule({ moduleId: module.id, courseId })}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
    </View>
  );
};

export default ModuleList;
